/**
 * Budget Service — Budget Management (011)
 * Rolling plans: spent is scored against calendar period bounds, not stored dates.
 */

import { supabase, getCurrentUserId } from '../lib/supabase';
import {
  Budget,
  BudgetOverview,
  BudgetAlert,
  BudgetPeriodType,
  CreateBudgetInput,
  UpdateBudgetInput,
  DashboardBudgetSummary,
  BudgetServiceResponse,
  AlertThresholds,
} from '../types/budget.types';
import { validateCreateBudget } from '../utils/budget-validation';
import {
  calculatePercentUsed,
  deriveCategoryLimitMetrics,
  sumSpentForCategories,
  DEFAULT_ALERT_THRESHOLDS,
} from '../utils/budget-calculations';
import { getPeriodBounds, scaleBudgetAmount, todayIsoDate } from '../utils/budget-period';

function mapBudgetRow(row: any, extras?: Partial<Budget>): Budget {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    periodType: row.period_type,
    status: row.status,
    endDate: row.end_date ?? null,
    alertThresholds: {
      warning: row.alert_threshold_warning,
      critical: row.alert_threshold_critical,
      limit: row.alert_threshold_limit,
    },
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...extras,
  };
}

async function fetchSpentByCategory(
  userId: string,
  dateFrom: string,
  dateTo: string
): Promise<Map<string, number>> {
  const { data, error } = await supabase
    .from('transactions')
    .select('amount, category_id, type, categories(exclude_from_spending)')
    .eq('user_id', userId)
    .gte('date', dateFrom)
    .lte('date', dateTo)
    .is('archived_at', null);

  if (error) throw error;

  const map = new Map<string, number>();
  for (const row of data ?? []) {
    const cat = row.categories as { exclude_from_spending?: boolean } | null;
    if (cat?.exclude_from_spending) continue;
    if (!row.category_id) continue;
    const amount = Math.abs(parseFloat(String(row.amount)) || 0);
    if (row.type === 'income') continue;
    map.set(row.category_id, (map.get(row.category_id) ?? 0) + amount);
  }
  return map;
}

async function fetchCategoryChildren(): Promise<Map<string, string[]>> {
  const { data } = await supabase.from('categories').select('id, parent_id');
  const children = new Map<string, string[]>();
  for (const row of data ?? []) {
    if (row.parent_id) {
      const list = children.get(row.parent_id) ?? [];
      list.push(row.id);
      children.set(row.parent_id, list);
    }
  }
  return children;
}

function emptyOverview(periodType: BudgetPeriodType, anchor?: string): BudgetOverview {
  const bounds = getPeriodBounds(periodType, anchor ?? todayIsoDate());
  return {
    periodType,
    periodStart: bounds.startDate,
    periodEnd: bounds.endDate,
    periodLabel: bounds.label,
    totalBudget: 0,
    totalSpent: 0,
    totalRemaining: 0,
    percentUsed: 0,
    percentRemaining: 100,
    categories: [],
  };
}

class BudgetService {
  async listBudgets(userId?: string): Promise<BudgetServiceResponse<Budget[]>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { data: budgets, error } = await supabase
        .from('budgets')
        .select('*')
        .eq('user_id', uid)
        .order('created_at', { ascending: false });

      if (error) return { error: { code: 'DB_ERROR', message: error.message } };

      const results: Budget[] = [];
      for (const row of budgets ?? []) {
        const enriched = await this.enrichBudget(row, uid);
        if (enriched.data) results.push(enriched.data);
      }
      return { data: results };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  /**
   * Enrich a plan with spent for a calendar view window, scaling stored limits
   * from the plan grain into the view grain.
   */
  private async enrichBudget(
    row: any,
    userId: string,
    viewPeriodType?: BudgetPeriodType,
    periodAnchor?: string
  ): Promise<BudgetServiceResponse<Budget>> {
    const planPeriodType = row.period_type as BudgetPeriodType;
    const viewType = viewPeriodType ?? planPeriodType;
    const anchor = periodAnchor ?? todayIsoDate();
    const bounds = getPeriodBounds(viewType, anchor);

    const thresholds: AlertThresholds = {
      warning: row.alert_threshold_warning,
      critical: row.alert_threshold_critical,
      limit: row.alert_threshold_limit,
    };

    const { data: limits } = await supabase
      .from('budget_category_limits')
      .select('*, categories(name)')
      .eq('budget_id', row.id);

    const spentMap = await fetchSpentByCategory(userId, bounds.startDate, bounds.endDate);
    const childMap = await fetchCategoryChildren();

    let totalBudget = 0;
    let planTotalBudget = 0;
    let totalSpent = 0;
    const categoryLimits = (limits ?? []).map((l: any) => {
      const planAmt = Number(l.amount);
      const budgetAmt = scaleBudgetAmount(planAmt, planPeriodType, viewType);
      const spent = sumSpentForCategories(spentMap, l.category_id, childMap.get(l.category_id) ?? []);
      const metrics = deriveCategoryLimitMetrics(spent, planAmt, budgetAmt, thresholds);
      if (metrics.countsTowardTotals) {
        totalBudget += budgetAmt;
        planTotalBudget += planAmt;
        totalSpent += spent;
      }
      return {
        id: l.id,
        budgetId: l.budget_id,
        categoryId: l.category_id,
        categoryName: l.categories?.name,
        amount: budgetAmt,
        planAmount: planAmt,
        spentAmount: spent,
        remainingAmount: budgetAmt - spent,
        percentUsed: metrics.percentUsed,
        status: metrics.status,
        statusLabel: metrics.statusLabel,
      };
    });

    const percentUsed = calculatePercentUsed(totalSpent, totalBudget);
    return {
      data: mapBudgetRow(row, {
        categoryLimits,
        categoryCount: categoryLimits.length,
        periodStart: bounds.startDate,
        periodEnd: bounds.endDate,
        periodLabel: bounds.label,
        totalBudget,
        planTotalBudget,
        totalSpent,
        totalRemaining: totalBudget - totalSpent,
        percentUsed,
      }),
    };
  }

  /**
   * All active plans, scored against the selected view calendar window with
   * limits scaled from each plan's stored period_type into the view grain.
   */
  async listBudgetsForPeriod(
    periodType: BudgetPeriodType,
    periodAnchor?: string,
    userId?: string
  ): Promise<BudgetServiceResponse<Budget[]>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const anchor = periodAnchor ?? todayIsoDate();
      const { data: budgets, error } = await supabase
        .from('budgets')
        .select('*')
        .eq('user_id', uid)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) return { error: { code: 'DB_ERROR', message: error.message } };

      const results: Budget[] = [];
      for (const row of budgets ?? []) {
        const enriched = await this.enrichBudget(row, uid, periodType, anchor);
        if (enriched.data) results.push(enriched.data);
      }
      return { data: results };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async getOverview(
    periodType: BudgetPeriodType,
    periodAnchor?: string,
    userId?: string
  ): Promise<BudgetServiceResponse<BudgetOverview>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const anchor = periodAnchor ?? todayIsoDate();
      const bounds = getPeriodBounds(periodType, anchor);

      const { data: budgets } = await supabase
        .from('budgets')
        .select('*')
        .eq('user_id', uid)
        .eq('status', 'active');

      if (!budgets?.length) {
        return { data: emptyOverview(periodType, anchor) };
      }

      let totalBudget = 0;
      let totalSpent = 0;
      const categories: BudgetOverview['categories'] = [];
      const childMap = await fetchCategoryChildren();
      const spentMap = await fetchSpentByCategory(uid, bounds.startDate, bounds.endDate);

      for (const budget of budgets) {
        const planPeriodType = budget.period_type as BudgetPeriodType;
        const thresholds: AlertThresholds = {
          warning: budget.alert_threshold_warning,
          critical: budget.alert_threshold_critical,
          limit: budget.alert_threshold_limit,
        };
        const { data: limits } = await supabase
          .from('budget_category_limits')
          .select('*, categories(name)')
          .eq('budget_id', budget.id);

        for (const l of limits ?? []) {
          const row = l as typeof l & { categories?: { name?: string } | null };
          const planAmt = Number(row.amount);
          const budgetAmt = scaleBudgetAmount(planAmt, planPeriodType, periodType);
          const spent = sumSpentForCategories(spentMap, row.category_id, childMap.get(row.category_id) ?? []);
          const metrics = deriveCategoryLimitMetrics(spent, planAmt, budgetAmt, thresholds);
          if (!metrics.countsTowardTotals) continue;
          totalBudget += budgetAmt;
          totalSpent += spent;
          categories.push({
            categoryId: row.category_id,
            categoryName: row.categories?.name ?? 'Unknown',
            budgetAmount: budgetAmt,
            spentAmount: spent,
            remainingAmount: budgetAmt - spent,
            percentUsed: metrics.percentUsed,
            status: metrics.status,
            statusLabel: metrics.statusLabel,
          });
        }
      }

      const percentUsed = calculatePercentUsed(totalSpent, totalBudget);
      return {
        data: {
          periodType,
          periodStart: bounds.startDate,
          periodEnd: bounds.endDate,
          periodLabel: bounds.label,
          totalBudget,
          totalSpent,
          totalRemaining: totalBudget - totalSpent,
          percentUsed,
          percentRemaining: 100 - percentUsed,
          categories,
        },
      };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async createBudget(input: CreateBudgetInput, userId?: string): Promise<BudgetServiceResponse<Budget>> {
    const parsed = validateCreateBudget(input);
    if (!parsed.success) {
      return { error: { code: 'VALIDATION_ERROR', message: parsed.error.issues[0]?.message ?? 'Invalid input' } };
    }

    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const thresholds = { ...DEFAULT_ALERT_THRESHOLDS, ...input.alertThresholds };
      const status = input.status ?? 'active';
      const { data: budget, error } = await supabase
        .from('budgets')
        .insert({
          user_id: uid,
          name: input.name,
          period_type: input.periodType,
          status,
          end_date: status === 'closed' ? input.endDate ?? null : null,
          alert_threshold_warning: thresholds.warning,
          alert_threshold_critical: thresholds.critical,
          alert_threshold_limit: thresholds.limit,
        })
        .select()
        .single();

      if (error || !budget) return { error: { code: 'DB_ERROR', message: error?.message ?? 'Insert failed' } };

      const limitRows = input.categoryLimits.map((l) => ({
        budget_id: budget.id,
        category_id: l.categoryId,
        amount: l.amount,
      }));
      const { error: limErr } = await supabase.from('budget_category_limits').insert(limitRows);
      if (limErr) return { error: { code: 'DB_ERROR', message: limErr.message } };

      return this.enrichBudget(budget, uid);
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async updateBudget(id: string, input: UpdateBudgetInput, userId?: string): Promise<BudgetServiceResponse<Budget>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (input.name) updates.name = input.name;
      if (input.periodType) updates.period_type = input.periodType;
      if (input.status) {
        updates.status = input.status;
        if (input.status === 'active') {
          updates.end_date = null;
        } else if (input.status === 'closed') {
          updates.end_date = input.endDate ?? null;
        }
      } else if (input.endDate !== undefined) {
        updates.end_date = input.endDate;
      }
      if (input.alertThresholds) {
        if (input.alertThresholds.warning != null) updates.alert_threshold_warning = input.alertThresholds.warning;
        if (input.alertThresholds.critical != null) updates.alert_threshold_critical = input.alertThresholds.critical;
        if (input.alertThresholds.limit != null) updates.alert_threshold_limit = input.alertThresholds.limit;
      }

      const { data: budget, error } = await supabase
        .from('budgets')
        .update(updates)
        .eq('id', id)
        .eq('user_id', uid)
        .select()
        .single();

      if (error || !budget) return { error: { code: 'NOT_FOUND', message: error?.message ?? 'Budget not found' } };

      if (input.categoryLimits) {
        await supabase.from('budget_category_limits').delete().eq('budget_id', id);
        await supabase.from('budget_category_limits').insert(
          input.categoryLimits.map((l) => ({ budget_id: id, category_id: l.categoryId, amount: l.amount }))
        );
      }

      return this.enrichBudget(budget, uid);
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async deleteBudget(id: string, userId?: string): Promise<BudgetServiceResponse<{ deleted: true }>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { error } = await supabase.from('budgets').delete().eq('id', id).eq('user_id', uid);
      if (error) return { error: { code: 'DB_ERROR', message: error.message } };
      return { data: { deleted: true } };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async getActiveAlerts(userId?: string): Promise<BudgetServiceResponse<BudgetAlert[]>> {
    try {
      const overview = await this.getOverview('monthly', undefined, userId);
      if (overview.error || !overview.data) return { data: [] };

      const alerts: BudgetAlert[] = [];
      for (const cat of overview.data.categories) {
        if (cat.status === 'over' || cat.status === 'critical') {
          alerts.push({
            id: `alert-${cat.categoryId}`,
            severity: cat.status === 'over' ? 'critical' : 'warning',
            title: `${cat.categoryName} budget alert`,
            message: `${cat.categoryName} budget at ${cat.percentUsed}% — ${cat.statusLabel}`,
            categoryName: cat.categoryName,
            thresholdLabel: cat.status === 'over' ? '100% Alert' : '90% Alert',
          });
        } else if (cat.status === 'on_track' && cat.percentUsed < 50) {
          alerts.push({
            id: `success-${cat.categoryId}`,
            severity: 'success',
            title: 'On track',
            message: `Great job on ${cat.categoryName}! You're well within your budget.`,
            categoryName: cat.categoryName,
            thresholdLabel: 'Success',
          });
        }
      }
      return { data: alerts };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async getDashboardSummary(userId?: string): Promise<BudgetServiceResponse<DashboardBudgetSummary>> {
    const overview = await this.getOverview('monthly', undefined, userId);
    const alerts = await this.getActiveAlerts(userId);
    if (overview.error || !overview.data) {
      return { error: overview.error ?? { code: 'UNKNOWN', message: 'Failed' } };
    }
    return {
      data: {
        totalBudget: overview.data.totalBudget,
        totalSpent: overview.data.totalSpent,
        totalRemaining: overview.data.totalRemaining,
        percentUsed: overview.data.percentUsed,
        topAlerts: (alerts.data ?? []).slice(0, 3).map((a) => ({ message: a.message, severity: a.severity })),
      },
    };
  }

  async getActiveBudgetsUsingCategory(categoryId: string, userId?: string): Promise<BudgetServiceResponse<string[]>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { data } = await supabase
        .from('budget_category_limits')
        .select('budget_id, budgets!inner(name, status, user_id)')
        .eq('category_id', categoryId)
        .eq('budgets.user_id', uid)
        .eq('budgets.status', 'active');

      const names = (data ?? []).map((r: any) => r.budgets?.name).filter(Boolean);
      return { data: names as string[] };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }
}

export const budgetService = new BudgetService();

export async function listBudgets(userId?: string) {
  return budgetService.listBudgets(userId);
}
export async function listBudgetsForPeriod(
  periodType: BudgetPeriodType,
  periodAnchor?: string,
  userId?: string
) {
  return budgetService.listBudgetsForPeriod(periodType, periodAnchor, userId);
}
export async function getOverview(periodType: BudgetPeriodType, periodAnchor?: string, userId?: string) {
  return budgetService.getOverview(periodType, periodAnchor, userId);
}
export async function createBudget(input: CreateBudgetInput, userId?: string) {
  return budgetService.createBudget(input, userId);
}
export async function updateBudget(id: string, input: UpdateBudgetInput, userId?: string) {
  return budgetService.updateBudget(id, input, userId);
}
export async function deleteBudget(id: string, userId?: string) {
  return budgetService.deleteBudget(id, userId);
}
export async function getActiveAlerts(userId?: string) {
  return budgetService.getActiveAlerts(userId);
}
export async function getDashboardSummary(userId?: string) {
  return budgetService.getDashboardSummary(userId);
}
